'use client';

import Accordion from "@/components/ui/Accordion";
import {motion} from "framer-motion";
import React, {useEffect, useMemo, useState} from "react";
import {headerVariants, pageVariants} from "@/lib/animations";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import type { FaqEntry } from "@/types/faq";

interface FAQItem {
    id: string;
    title: string;
    content: string;
}

interface FaqsProps {
    generalFaqs?: FaqEntry[];

    faqIds?: number[];
    language?: string;
    languageCode?: string;
    languageId?: number;

    title?: string;
    /** Tur detayda çift H1 önlemek için h2 kullanın */
    headingLevel?: 'h1' | 'h2';

    enableClientFetch?: boolean;
    __pageContext?: {
        language?: string;
        languageCode?: string;
        languageId?: number;
    };
}

// Kategorileri kaldırdık - sadece düz FAQ listesi
const buildFaqList = (generalFaqs: FaqEntry[]): FAQItem[] => {
    return generalFaqs.map((faq) => ({
        id: `faq-${faq.id}`,
        title: faq.question,
        content: faq.answer,
    }));
};

export default function Faqs({
    generalFaqs: initialGeneralFaqs = [],
    faqIds,
    language,
    languageCode,
    languageId,
    title,
    headingLevel = 'h1',
    enableClientFetch = false,
    __pageContext,
}: FaqsProps){
    const { variables: contextVariables, getVariable: getContextVariable } = useLanguageVariables();
    const resolvedTitle = title ?? getContextVariable('faqs', 'FAQs');
    const [openAccordion, setOpenAccordion] = useState<string | null>(null);
    const [generalFaqs, setGeneralFaqs] = useState<FaqEntry[]>(initialGeneralFaqs);
    const [fetchedFaqs, setFetchedFaqs] = useState<FAQItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // Language variables için state
    const [variables, setVariables] = useState<Record<string, string>>({});

    const resolvedLanguageCode =
        language ||
        languageCode ||
        __pageContext?.language ||
        __pageContext?.languageCode ||
        undefined;

    const resolvedLanguageId =
        languageId ||
        __pageContext?.languageId ||
        undefined;

    const selectedFaqIds = useMemo(
        () => (Array.isArray(faqIds) ? faqIds.filter((id): id is number => Number.isInteger(id)) : []),
        [faqIds],
    );
    
    // Props'tan veri geldiyse client-side fetch yapma
    const hasInitialData = initialGeneralFaqs.length > 0;
    const shouldFetch = enableClientFetch && !hasInitialData && selectedFaqIds.length > 0;

    // Helper function to get variable with fallback
    const getVariable = (key: string, fallback: string): string => {
        return variables[key] || fallback;
    };

    const handleToggle = (id: string) => {
        setOpenAccordion(prev => prev === id ? null : id);
    };

    // FAQ listesini oluştur (gruplama yok)
    const faqsFromProps = useMemo(() => buildFaqList(generalFaqs), [generalFaqs]);
    // shouldFetch true ise fetchedFaqs kullan, değilse props'tan gelen veriyi kullan
    const allFaqs = shouldFetch && fetchedFaqs.length > 0 ? fetchedFaqs : faqsFromProps;

    const hasData = allFaqs.length > 0;
    // missingSelection: faqIds yoksa veya boşsa VE sayfa ile ilişkili FAQ yoksa
    // Ama sayfa ile ilişkili FAQ'ler backend'den otomatik gelir, bu yüzden sadece client-side fetch durumunda kontrol et
    const missingSelection = shouldFetch && selectedFaqIds.length === 0 && !hasInitialData;

    const renderColumns = (items: FAQItem[]) => {
        const left = items.filter((_, i) => i % 2 === 0);
        const right = items.filter((_, i) => i % 2 === 1);

        return (
            <div className="flex flex-col lg:flex-row gap-6">
                <div className="flex-1 flex flex-col gap-6">
                    {left.map((item) => (
                        <Accordion key={item.id} item={item} isOpen={openAccordion === item.id} onToggle={() => handleToggle(item.id)} />
                    ))}
                </div>
                <div className="flex-1 flex flex-col gap-6">
                    {right.map((item) => (
                        <Accordion key={item.id} item={item} isOpen={openAccordion === item.id} onToggle={() => handleToggle(item.id)} />
                    ))}
                </div>
            </div>
        );
    };

    // Language variables - Context'ten al (duplicate fetch'i önlemek için)
    useEffect(() => {
        if (contextVariables && Object.keys(contextVariables).length > 0) {
            setVariables(contextVariables);
        }
    }, [contextVariables]);

    useEffect(() => {
        if (!shouldFetch) {
            setLoading(false);
            setError(null);
            setFetchedFaqs([]);
            return;
        }

        const abortController = new AbortController();
        const fetchFaqs = async () => {
            try {
                setLoading(true);
                setError(null);

                // API parametrelerini oluştur
                // site_id göndermiyoruz - Laravel API key'den otomatik bulacak
                const params = new URLSearchParams();
                
                // faqIds varsa öncelikli olarak kullan
                if (selectedFaqIds.length > 0) {
                    selectedFaqIds.forEach(id => params.append('faq_ids[]', String(id)));
                }
                
                if (resolvedLanguageCode) {
                    params.set('language', resolvedLanguageCode);
                } else if (resolvedLanguageId) {
                    params.set('language_id', String(resolvedLanguageId));
                }

                // Sayfa slug'ını client-side'da window.location.pathname'den al
                // Server-side'da context'ten otomatik gelir
                if (typeof window !== 'undefined') {
                    const currentPath = window.location.pathname;
                    if (currentPath && currentPath !== '/') {
                        params.set('page_slug', currentPath);
                    }
                }

                const response = await fetch(`/api/faqs?${params.toString()}`, {
                    signal: abortController.signal,
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const errorMessage = errorData.message || errorData.error || `API error: ${response.status}`;
                    throw new Error(errorMessage);
                }

                const data = await response.json();
                // API response formatı: { data: { language, categories, general_faqs } }
                const apiFaqs: FaqEntry[] = data?.data?.general_faqs || [];

                // State'i güncelle
                setGeneralFaqs(apiFaqs);

                // FAQ listesini oluştur (gruplama yok)
                const built = buildFaqList(apiFaqs);
                setFetchedFaqs(built);
            } catch (err) {
                if (abortController.signal.aborted) {
                    return;
                }
                console.error('FAQ fetch error:', err);
                const errorMessage = err instanceof Error ? err.message : getVariable('faqs_load_error', 'FAQ data could not be loaded');
                setError(errorMessage);
                setFetchedFaqs([]);
            } finally {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchFaqs();
        return () => abortController.abort();
    }, [shouldFetch, resolvedLanguageCode, resolvedLanguageId, selectedFaqIds]);

    if (loading) {
        return (
            <section className="bg-white pb-[60px]">
                <div className="container mx-auto py-20 text-center text-gray-500">
                    {getVariable('faqs_loading', 'Loading FAQs...')}
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="bg-white pb-[60px]">
                <div className="container mx-auto py-20 text-center text-red-500">
                    {error}
                </div>
            </section>
        );
    }

    // missingSelection: Sadece client-side fetch durumunda ve hiç veri yoksa göster
    // Server-side'da sayfa ile ilişkili FAQ'ler otomatik gelir, bu yüzden bu kontrolü sadece client-side fetch için yap
    if (missingSelection && !hasInitialData) {
        return (
            <section className="bg-white pt-[75px] pb-[60px]">
                <div className="container mx-auto py-20 text-center text-alsero-light-gray border-2 border-dashed border-gray-200 rounded-2xl">
                    {getVariable('faqs_no_selection', 'No FAQ selection made for this component. Please select the component in the page editor and use the "Select Questions" button.')}
                </div>
            </section>
        );
    }

    return (
        <motion.section
            className="bg-white pt-[75px] pb-[60px]"
            initial="hidden"
            animate="visible"
            variants={pageVariants}
        >
            <div className="container mx-auto">
                <div className="flex flex-col gap-10">
                    <motion.div
                        className="flex flex-col gap-6 items-center"
                        variants={headerVariants}
                    >
                        {headingLevel === 'h2' ? (
                        <motion.h2
                            className="font-bold text-alsero-dark-gray text-center text-2xl sm:text-3xl md:text-4xl lg:text-4xl xl:text-5xl leading-tight"
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ type: "spring" as const, stiffness: 100, damping: 15 }}
                            data-prop-path="title"
                        >
                            {resolvedTitle}
                        </motion.h2>
                        ) : (
                        <motion.h1
                            className="font-bold text-alsero-dark-gray text-center text-2xl sm:text-3xl md:text-4xl lg:text-4xl xl:text-5xl leading-tight"
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ type: "spring" as const, stiffness: 100, damping: 15 }}
                            data-prop-path="title"
                        >
                            {resolvedTitle}
                        </motion.h1>
                        )}
                    </motion.div>

                    {!hasData && (
                        <motion.div
                            className="mt-12 text-center text-alsero-light-gray"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            {getVariable('faqs_no_faqs', 'No FAQs found for this language yet.')}
                        </motion.div>
                    )}

                    {hasData && (
                        <motion.div
                            className="mt-4"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            {renderColumns(allFaqs)}
                        </motion.div>
                    )}
                </div>
            </div>
        </motion.section>
    )
}